import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Autocomplete } from '@atoms';
import { useCustomerId, useAreasByCustomer, useIsAreasAdmin } from '@hooks';
import { Area } from '@modules/clients/modules/CompanyDetails/components/AreasList/AreasList';
interface Option {
  id: string;
  label: string;
}
interface AreasProps {
  value?: string | Option;
  onChange: (event: React.SyntheticEvent, value: Option) => void;
  onClear?: () => void;
  optionWithoutAreas?: boolean;
  parseOption?: (value: Area) => Option;
  disabled?: boolean;
  customerId?: number;
  placeholder?: string;
  error?: string;
}

/**
 * Areas component for selecting areas associated with a customer
 * @component
 *
 * @example
 * <Areas
 *   value={selectedArea}
 *   onChange={handleAreaChange}
 *   onClear={handleClear}
 *   disabled={false}
 *   parseOption = ({id, fantasyName}) => ({ id, label: fantasyName }),
 *   optionWithoutAreas
 * />
 */
const SelectAreas = ({
  value,
  onChange,
  optionWithoutAreas,
  onClear,
  parseOption = ({ id, fantasyName }) => ({ id: id, label: fantasyName }),
  disabled = false,
  customerId,
  placeholder = 'COMMON:AREA_LABEL',
  error,
}: AreasProps): JSX.Element => {
  const { t } = useTranslation();
  const { customerId: customerIdStore } = useCustomerId();
  const { data: areas } = useAreasByCustomer({
    customerId: customerId || customerIdStore,
  });
  const { isAreasAdmin } = useIsAreasAdmin();

  const optionWithoutAreasAux =
    optionWithoutAreas === undefined ? isAreasAdmin : optionWithoutAreas;
  const areasAux = useMemo(
    () => [
      ...(optionWithoutAreasAux
        ? [
            {
              id: 'none',
              name: 'none',
              fantasyName: t('COMMON:OPTION_WITHOUT_AREAS'),
              balance: 0,
              costCenter: null,
            },
          ]
        : []),
      ...areas,
    ],
    [optionWithoutAreasAux, areas, t]
  );
  const currentValue = useMemo(() => {
    if (typeof value === 'string') {
      const areaAux = areasAux.find((item) => {
        return parseOption(item).id === value;
      });
      return areaAux ? parseOption(areaAux) : null;
    }

    return value;
  }, [areasAux, value, parseOption]);

  return (
    <Autocomplete
      key={String(customerId)}
      onChange={onChange}
      value={currentValue ? currentValue : null}
      items={areasAux}
      placeholder={t(placeholder)}
      getOptionLabel={(value: Option) => value.label}
      parseOption={parseOption}
      isOptionEqualToValue={(option: Option, value: Option) => option.id === value.id}
      disabled={areasAux.length === 0 || disabled}
      onClear={onClear}
      clearIcon={onClear !== undefined}
      error={error}
    />
  );
};

export default SelectAreas;
